import React from "react";
import { isQualityMock } from "./qualityApi";

/* ── vocabulary ────────────────────────────────────────────────────────────
 * Severity labels and colours are the Customer Support ones (sla.py /
 * customerSupportApi.js SEVERITY_DOT) so a P1 means the same thing on every
 * screen. Stages follow the production bug lifecycle in the design plan.
 */

export const SEVERITY = {
    P0: "Operations stopped",
    P1: "Major function broken",
    P2: "Workaround available",
    P3: "Minor / cosmetic",
};

export const STAGES = [
    { key: "raised", label: "Raised" },
    { key: "classified", label: "Classified" },
    { key: "traced", label: "Traced" },
    { key: "rca", label: "Root cause" },
    { key: "fix_in_progress", label: "Fix in progress" },
    { key: "regression", label: "Regression" },
    { key: "qa_approved", label: "QA approved" },
    { key: "released", label: "Released" },
    { key: "verified", label: "Verified" },
    { key: "closed", label: "Closed" },
];

export const SOURCES = {
    customer_support: { label: "Customer Support", cls: "qa-chip--blue" },
    error_tracking: { label: "Error Tracking", cls: "qa-chip--red" },
    internal: { label: "Internal team", cls: "qa-chip--accent" },
    monitoring: { label: "Monitoring", cls: "qa-chip--amber" },
};

export const RCA_CATEGORIES = [
    "Requirement gap", "Development error", "Missing test case",
    "Test passed incorrectly", "Environment / config", "Data", "Third party",
];
export const PROCESS_STEPS = ["Requirement", "Development", "Test design", "Test execution", "Approval"];
export const UNTRACED_REASONS = ["Configuration", "Data issue", "Third party", "Infrastructure"];

// Suggested Test Type values; any short value is accepted (sheets bring their own).
export const CASE_TYPES = ["Positive", "Negative", "Boundary", "Functional", "UI", "Integration", "Smoke", "Regression"];
export const SEVERITY_LEVELS = ["Critical", "High", "Medium", "Low"];
export const TEST_RESULTS = ["Pass", "Fail", "Blocked", "Not Run"];

// The QA team's sheet columns, in order; the import template uses these.
export const SHEET_COLUMNS = [
    "S.No", "Module", "Sub Module", "Screen", "Test Case Scenario", "Test Case Description", "Pre-Condition",
    "Test Steps", "Test Type", "Severity", "Test Data", "Expected Result", "Actual Result", "Result",
];

export const SeverityChip = ({ value }) =>
    value ? (
        <span className={`qa-chip ${value === "Critical" ? "qa-chip--red" : value === "High" ? "qa-chip--amber" : value === "Medium" ? "qa-chip--blue" : ""}`}>
            {value}
        </span>
    ) : <span className="qa-faint">—</span>;

/**
 * A test case's result for tables: the latest regression run wins; otherwise
 * the result recorded by hand / imported from the sheet.
 */
export const CaseResult = ({ testCase }) => {
    const run = testCase.last_result;
    if (run) {
        return <><ResultPill value={run.result} /> <span className="qa-hint">{run.version}</span></>;
    }
    const manual = { Pass: "pass", Fail: "fail", Blocked: "blocked", "Not Run": "not_run" }[testCase.result];
    return manual ? <ResultPill value={manual} /> : <span className="qa-pill qa-pill--warn">Never run</span>;
};

export const stageIndex = (key) => STAGES.findIndex((s) => s.key === key);
export const stageLabel = (key) => (STAGES[stageIndex(key)] || {}).label || key;

/* ── permissions ───────────────────────────────────────────────────────────
 * Same mechanism as the rest of FixDesk: LoginRole.permissions is stored as
 * an array of granted names in localStorage.userPermissions at login. The mock
 * grants everything so the screens can be reviewed before roles are set up.
 */
export const QUALITY_PERMISSIONS = [
    "Quality", "Log Production Bug", "Manage Releases",
    "Run Regression", "Manage Test Cases", "Approve RCA",
];

export const hasPerm = (name) => {
    if (isQualityMock()) return true;
    try {
        const list = JSON.parse(localStorage.getItem("userPermissions") || "[]");
        return Array.isArray(list) && list.includes(name);
    } catch {
        return false;
    }
};

/* ── formatting ────────────────────────────────────────────────────────── */
const pad = (n) => String(n).padStart(2, "0");

// DD-MM-YYYY, the format used across FixDesk tables (CommonFunction.formatDate).
export const fmtDate = (value, withTime = false) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const base = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    return withTime ? `${base} ${pad(d.getHours())}:${pad(d.getMinutes())}` : base;
};

// "07 Oct", as the sprint table's due-date column shows it.
export const fmtShort = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

/* ── small components ──────────────────────────────────────────────────── */
export const MockBanner = () =>
    isQualityMock() ? (
        <div className="qa-mock">
            <b>Sample data</b> The Quality backend is not connected, so these screens use an
            in-memory mock. Nothing is saved. Remove <code>localStorage.qualityMock</code> to use the API.
        </div>
    ) : null;

export const SevChip = ({ value, long }) =>
    value ? (
        <span className={`qa-sev qa-sev--${value}`}>
            {value}
            {long && <em> · {SEVERITY[value]}</em>}
        </span>
    ) : null;

export const StagePill = ({ value }) => {
    const i = stageIndex(value);
    const cls = i >= 9 ? "success" : i >= 6 ? "info" : i >= 4 ? "accent" : i >= 2 ? "purple" : "warn";
    return <span className={`qa-pill qa-pill--${cls}`}>{stageLabel(value)}</span>;
};

export const SourceChip = ({ value }) => {
    const s = SOURCES[value] || { label: value, cls: "" };
    return <span className={`qa-chip ${s.cls}`}>{s.label}</span>;
};

const RESULT = {
    pass: ["Pass", "success"],
    fail: ["Fail", "danger"],
    blocked: ["Blocked", "warn"],
    not_run: ["Not run", "muted"],
};
export const ResultPill = ({ value }) => {
    const [label, cls] = RESULT[value] || [value || "—", "muted"];
    return <span className={`qa-pill qa-pill--${cls}`}>{label}</span>;
};

const RELEASE = {
    planned: ["Planned", "muted"],
    in_regression: ["In regression", "warn"],
    validated: ["Validated", "info"],
    deployed: ["Deployed", "success"],
    rolled_back: ["Rolled back", "danger"],
};
export const ReleasePill = ({ value }) => {
    const [label, cls] = RELEASE[value] || [value, "muted"];
    return <span className={`qa-pill qa-pill--${cls}`}>{label}</span>;
};

export const Prio = ({ value }) => {
    const color = value === "High" ? "var(--status-red)" : value === "Low" ? "var(--status-green)" : "#f5a623";
    return <span className="qa-prio" style={{ color }}>{value}</span>;
};

export const TypeChip = ({ value }) => (
    <span className={`qa-chip ${value === "Negative" ? "qa-chip--amber" : value === "Boundary" ? "qa-chip--purple" : "qa-chip--blue"}`}>
        {value}
    </span>
);

export const CaseStatus = ({ value }) => (
    <span className={`qa-pill qa-pill--${value === "Active" ? "success" : value === "Draft" ? "warn" : "muted"}`}>{value}</span>
);

const AVATAR_COLORS = ["#7c5cfc", "#1976d2", "#e53935", "#43a047", "#f5a623", "#8e24aa", "#00897b"];
const colorFor = (name) => {
    let h = 0;
    for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
export const People = ({ names = [] }) => {
    const list = names.filter(Boolean);
    if (!list.length) return <span className="qa-faint">—</span>;
    return (
        <span className="qa-people">
            <span className="qa-avs">
                {list.map((n) => (
                    <span key={n} className="qa-av" style={{ background: colorFor(n) }} title={n}>
                        {n.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                ))}
            </span>
            <span>{list.join(", ")}</span>
        </span>
    );
};

export const Kpi = ({ value, label, color, fill }) => (
    <div className="qa-kpi" style={{ "--kc": color }}>
        <div className="qa-kpi__v">{value}</div>
        <div className="qa-kpi__l">{label}</div>
        <div className="qa-kpi__bar">
            <i style={{ width: `${Math.max(0, Math.min(100, fill || 0))}%` }} />
        </div>
    </div>
);

// Same 48px ring as the Sprint dashboard's "Total Tickets" tile.
export const RingKpi = ({ percent, value, label }) => {
    const c = 2 * Math.PI * 15.5;
    const p = Math.max(0, Math.min(100, percent || 0));
    return (
        <div className="qa-kpi qa-kpi--ring">
            <svg viewBox="0 0 36 36" aria-hidden="true">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--border-subtle)" strokeWidth="3" />
                <circle
                    cx="18" cy="18" r="15.5" fill="none" stroke="var(--accent)" strokeWidth="3"
                    strokeDasharray={`${(c * p) / 100} ${c}`} transform="rotate(-90 18 18)" strokeLinecap="round"
                />
                <text x="18" y="20.5" textAnchor="middle" fontSize="7" fontWeight="600" fill="var(--accent)">
                    {Math.round(p)}%
                </text>
            </svg>
            <div>
                <div className="qa-kpi__v">{value}</div>
                <div className="qa-kpi__l">{label}</div>
            </div>
        </div>
    );
};

export const Bars = ({ rows, color = "var(--accent)", labelWidth = 110 }) => {
    const max = Math.max(1, ...rows.map((r) => r.value));
    return (
        <div className="qa-bars">
            {rows.map((r) => (
                <div key={r.label} className="qa-bars__r" style={{ gridTemplateColumns: `${labelWidth}px minmax(0,1fr) 36px` }}>
                    <span>{r.label}</span>
                    <div className="qa-bars__t">
                        <i style={{ width: `${(r.value / max) * 100}%`, background: r.value ? r.color || color : "transparent" }} />
                    </div>
                    <span className="qa-num">{r.value}</span>
                </div>
            ))}
        </div>
    );
};

export const Modal = ({ title, onClose, children, footer, wide }) => (
    <div className="qa-ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className={`qa-modal${wide ? " qa-modal--wide" : ""}`} role="dialog" aria-label={typeof title === "string" ? title : undefined}>
            <div className="qa-m-head">
                <h3>{title}</h3>
                <button type="button" className="qa-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <div className="qa-m-body">{children}</div>
            {footer && <div className="qa-m-foot">{footer}</div>}
        </div>
    </div>
);
